import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { XIcon } from "./icons";

/**
 * Global toast layer (§2.7/§3.14): bottom-right, auto-dismiss, manual close.
 * Usage:
 *   const toast = useToast();
 *   toast.success("Service request saved");
 *   toast.error("Couldn't save the quote — try again");
 * The kit's replacement for scattered cogoToast calls; legacy code keeps
 * cogoToast until each surface is rebuilt.
 */

const ToastContext = createContext(null);

const AUTO_DISMISS_MS = 5000;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind, message) => {
      idRef.current += 1;
      const id = idRef.current;
      setToasts((list) => [...list, { id, kind, message }]);
      window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
      return id;
    },
    [dismiss]
  );

  const api = useMemo(
    () => ({
      success: (m) => push("success", m),
      error: (m) => push("error", m),
      info: (m) => push("info", m),
      warning: (m) => push("warning", m),
      dismiss
    }),
    [push, dismiss]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="fk-toaster" role="region" aria-label="Notifications">
          {toasts.map((t) => (
            <div key={t.id} className={`fk-toast fk-toast--${t.kind}`} role="status">
              <span className="fk-toast__message">{t.message}</span>
              <button
                type="button"
                className="fk-icon-btn"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
              >
                <XIcon size={14} />
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}
